import { of, throwError } from 'rxjs'
import { map, mergeMap, catchError } from 'rxjs/operators'
import Constants from 'api-constants'
import { serialize } from 'helpers/index.js'
import ConfigHelper from 'framework/helpers/config'
import DataHelper from 'framework/helpers/data'
import Fetch from '../fetch'
import AuthType from 'framework/helpers/auth'
import { PlaybackAttributes } from 'models'
import VoltError from 'VoltError'
import ReportActivityApi from '../ReportActivityApi'

/**
 * Deals with Playback requirements (session creation, tracking etc)
 */
export default class PlayerApi extends Fetch {
    static TAG = 'PlayerApi'
    static PLAYBACK_TAG = '[_PLAYBACK_]'

    constructor(config, otherApis) {
        super(config, otherApis)
        this.reportActivityApi = otherApis.reportActivityApi
        this.metaApi = otherApis.metaApi
    }
    /**
     * This is internal method which retrieves the Nagra content token to be used by Nagra SSM Player
     * The Nagra Player handle the remaining call (Session, Devices, drm)
     *
     * @param {Object} args
     * @param {String} args.contentId
     * @param {String} args.playbackUrl
     * @param {String} args.assetId
     * @param {String} [args.drmProvider]
     * @param {String} [args.isChromecastConnected]
     * @return {Observable<SessionData>}
     */
    _getContentToken({
        contentId,
        playbackUrl,
        assetId,
        drmProvider,
        isTrailer,
        isChromecastConnected,
    }) {
        const {
            tenantId,
            widevineLicenseUrl,
            SSMLicenseUrl,
            fairplayLicenseUrl,
            fairplaySSMLicenseUrl,
            useCdnToken = false,
        } = ConfigHelper.getInstance().getConfig('nagra')
        if (!contentId) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_CONTENT_ID_ERROR, {
                    extraLog: 'Missing DRM ID Coming from Nagra',
                })
            )
        }
        if (!tenantId) {
            return throwError(
                new VoltError(VoltError.codes.MISSING_TENANT_ID, {
                    extraLog: 'Missing tenant ID',
                })
            )
        }

        if (!playbackUrl) {
            this.logger.warn(
                'The Manifest URL is empty while starting a playback, this should be reported to the backend as it will probably cause a failure starting the playback'
            )
            return throwError(
                new VoltError(VoltError.codes.METADATA_MISSING_MANIFEST_FILE_FROM_BACKEND, {
                    extraLog: 'Missing Playback/Manifest URL from the manifest',
                    backend: this.backendName,
                })
            )
        }

        const drmType = this._whatIsMyDrm(isChromecastConnected)

        this.logger.info(PlayerApi.PLAYBACK_TAG, `Getting the content token... for ${drmType} drm`)

        let endpointUrl

        const useTokenization = this.config.nagra.useTokenization ?? false

        if (useCdnToken) {
            endpointUrl = `${this.config.urls.apiUrl}${
                useTokenization ? `/urlbuilder/v1/playout/content/token` : `/ias/v2/cdn_token`
            }${serialize({
                content_id: contentId,
                type: 'device',
                content_URL: playbackUrl,
                // According to Broadpeak/Starhub the WAN IP Address is not needed, so we hardcode a dummy one to make API working fine
                ip: '192.168.1.1',
            })}`
        } else {
            const serializeAttributes = useTokenization
                ? {
                      content_id: contentId,
                      type: 'device',
                      content_URL: playbackUrl,
                  }
                : {
                      content_id: contentId,
                      type: 'device',
                  }
            endpointUrl = `${this.config.urls.apiUrl}${
                useTokenization ? `/urlbuilder/v1/playout/content/token` : `/ias/v2/content_token`
            }${serialize(serializeAttributes)}`
        }

        return this.fetch({
            url: endpointUrl,
            method: useTokenization ? 'GET' : 'POST',
            headers: {
                'Content-Type': Constants.httpHeader.ContentType.APPLICATION_JSON,
                'nv-tenant-id': tenantId,
                Authorization: AuthType.BearerToken(
                    DataHelper.getInstance().getSecondaryAccessToken()
                ),
            },
            log: useCdnToken ? 'GET [CDN] CONTENT TOKEN' : 'GET CONTENT TOKEN',
        }).pipe(
            mergeMap(({ response } = {}) => {
                if (useTokenization) {
                    if (
                        !response ||
                        !response.contentAuthToken ||
                        !response.contentAuthToken.content_token
                    ) {
                        return throwError(
                            new VoltError(VoltError.codes.PLAYER_ERROR_DUE_TO_INVALID_TOKEN, {
                                extraLog: 'No content_token was found',
                                backend: this.backendName,
                            })
                        )
                    }
                } else {
                    if (!response || !response.content_token) {
                        return throwError(
                            new VoltError(VoltError.codes.PLAYER_ERROR_DUE_TO_INVALID_TOKEN, {
                                extraLog: 'No content_token was found',
                                backend: this.backendName,
                            })
                        )
                    }
                }

                if (useTokenization) {
                    if (response.playbackUrl) {
                        const cdn_token = response.cdnAccessToken
                        if (cdn_token) {
                            playbackUrl = response.playbackUrl
                        } else {
                            this.logger.warn(
                                PlayerApi.PLAYBACK_TAG,
                                'Missing CDN TOKEN from response...'
                            )
                        }
                    }
                } else {
                    if (useCdnToken) {
                        const cdn_token = response.cdn_token
                        if (cdn_token) {
                            playbackUrl = `${playbackUrl}${serialize(
                                {
                                    primaryToken: cdn_token,
                                },
                                playbackUrl?.includes('?') ? '&' : '?'
                            )}`
                        } else {
                            this.logger.warn(
                                PlayerApi.PLAYBACK_TAG,
                                'Missing CDN TOKEN from response...'
                            )
                        }
                    }
                }

                const isFairplay = PlaybackAttributes.DRM_TYPE.FAIRPLAY === drmType

                this.logger.info(PlayerApi.PLAYBACK_TAG, 'Content token received...')
                return of(
                    new PlaybackAttributes({
                        url: playbackUrl,
                        assetId: assetId,
                        keepAlive: null, // No Keep alive as it is managed by Nagra Player directly
                        // trick: the session id is needed just for executing all session management functions properly
                        sessionId: 'FAKE_SESSION_ID_FOR_REPORTING',
                        drm: {
                            // Based on this documentation
                            // https://docs.nagra.com/doc/cps/cpsand550/android-sdk-5-player-features/widevine-protected-playback
                            // WARNING: The duration of the Token is 300 secondes, it means that the player should starts in the following 5 minutes
                            headers: {
                                Accept: 'application/octet-stream',
                                'Content-Type': 'application/octet-stream',
                                'nv-tenant-id': tenantId,
                                'nv-authorizations': useTokenization
                                    ? response.contentAuthToken.content_token
                                    : response.content_token,
                            },
                            licenseUrl: isFairplay ? fairplayLicenseUrl : widevineLicenseUrl,
                            SSMLicenseUrl: isFairplay ? fairplaySSMLicenseUrl : SSMLicenseUrl,
                        },
                        drmType,
                        drmProvider,
                        isTrailer,
                    })
                )
            }),
            catchError((error = {}) =>
                throwError(
                    new VoltError(
                        (() => {
                            switch (error.code) {
                                case VoltError.codes.AUTH_UNAUTHORIZED_USER.code: // Occurs when the user try to get a content token but he is not entitled
                                case VoltError.codes.AUTH_OAUTH_EXPIRED_TOKEN.code: // Occurs when Nagra Token is no longer valid
                                    return VoltError.codes.PLAYER_ENTITLEMENT_REVOKED
                                default:
                                    return error
                            }
                        })(),
                        {
                            inheritedError: error,
                        }
                    )
                )
            )
        )
    }

    /**
     * Get the url a associated with multicam url manifest
     *
     * @param {Object} data
     * @param {String} data.programId
     * @param {String} data.drmId
     * @param {String} data.playbackUrl
     * @param {String} data.platformId
     * @param {String} [data.drmProvider]
     * @param {String} [data.isChromecastConnected]
     * @return {Observable<SessionData>}
     */
    getMulticamStreamUrl(args = {}) {
        const {
            programId,
            drmId,
            playbackUrl,
            platformId,
            drmProvider,
            isChromecastConnected,
            rollingBufferTime,
            rollingBufferEnd,
        } = args

        let manifestUrl = playbackUrl
        this.reportActivityApi.setContentType = ReportActivityApi.CONTENT_TYPE.LIVE_EVENT
        this.reportActivityApi.setContentId = programId

        if (rollingBufferTime && rollingBufferEnd) {
            manifestUrl = this._buildBufferedUrl(playbackUrl, rollingBufferTime, rollingBufferEnd)
        }
        return this._getContentToken({
            contentId: drmId,
            playbackUrl: manifestUrl,
            assetId: platformId,
            drmProvider,
            isChromecastConnected,
        })
    }

    /**
     * Get the url a associated Live manifest
     *
     * @param {Object} data
     * @param {String} data.platformChannelId
     * @param {String} data.channelTechnicalId
     * @param {String} data.playbackUrl
     * @param {String} data.playbackId
     * @param {String} data.programId
     * @param {String} data.startTime
     * @param {String} [data.drmProvider]
     * @param {String} [data.isChromecastConnected]
     * @return {Observable<SessionData>}
     */
    getLiveStreamUrl(args = {}) {
        const {
            playbackId,
            playbackUrl,
            programId,
            platformChannelId,
            channelTechnicalId,
            drmProvider,
            startTime,
            channelSourceId,
            isChromecastConnected,
        } = args

        return this._resolveChannelInfo({
            isChromecastConnected,
            channelSourceId,
            channelTechnicalId: channelTechnicalId || platformChannelId,
            playbackId: playbackId,
            playbackUrl: playbackUrl,
        }).pipe(
            mergeMap(({ platformId, drmId, manifestUrl } = {}) => {
                this.reportActivityApi.setContentType = ReportActivityApi.CONTENT_TYPE.LIVE_EVENT
                this.reportActivityApi.setContentId = programId
                return this._getContentToken({
                    contentId: drmId,
                    playbackUrl: this._buildBufferedUrl(manifestUrl, startTime),
                    assetId: platformId,
                    drmProvider,
                    isChromecastConnected,
                })
            })
        )
    }

    /**
     * Get the url associated Vod manifest
     * @param {Object} args
     * @param {String} args.playbackContentId can be the id of the trailer or that of the main playback
     * @param {Object} args.streams
     * @param {String} args.titleId Id of the title/offer - only used for vubiquity
     * @param {String} args.programSourceId
     * @param {Boolean} args.isTrailer
     * * @param {Boolean} [data.isChromecastConnected]
     * @return {Observable<SessionData>}
     */
    getVodStreamUrl({
        playbackContentId,
        streams,
        titleId,
        programSourceId,
        isTrailer,
        isChromecastConnected,
    } = {}) {
        return this._resolveVodInfo({
            titleId,
            playbackContentId,
            programSourceId,
            streams,
            isChromecastConnected,
        }).pipe(
            mergeMap(({ vodPlatformId, vodSourceId, vodPlaybackContentId, vodStreams } = {}) => {
                const stream = (vodStreams || []).find(
                    (stream) => stream.uri === vodPlaybackContentId
                )
                if (!stream) {
                    return throwError(
                        new VoltError(VoltError.codes.MISSING_MEDIA_FILES, {
                            inheritedError: 'No stream or no matching stream was found',
                        })
                    )
                }
                const { uri, playback_id } = stream || {}

                this.reportActivityApi.setContentType = ReportActivityApi.CONTENT_TYPE.VOD_ED
                this.reportActivityApi.setContentId = vodSourceId || vodPlatformId

                return this._getContentToken({
                    contentId: playback_id,
                    playbackUrl: uri,
                    assetId: uri,
                    isTrailer,
                    isChromecastConnected,
                })
            })
        )
    }

    /**
     * Gets the manifest url for playing a Dvr stream
     *
     * @param {Object} args
     * @param {String|Number} args.recordingId Dvr ID
     * @param {String} data.channelTechnicalId
     * @param {String} args.playbackUrl
     * @returns {Observable<SessionData>}
     */
    getDvrStreamUrl({
        recordingId,
        isOngoing,
        playbackId,
        playbackUrl,
        programId,
        startTime,
        endTime,
        channelTechnicalId,
        platformChannelId,
        channelPlaybackUrl,
        isChromecastConnected,
        chromecastPlaybackId,
        chromecastPlaybackUrl,
    }) {
        if (isOngoing) {
            this.logger.info('request startOver URL ', {
                channelId: channelTechnicalId || platformChannelId,
                programId,
            })

            return this.getRollingBufferStreamUrl({
                playbackUrl: channelPlaybackUrl,
                playbackId,
                channelId: channelTechnicalId || platformChannelId,
                programId,
                rollingBufferTime: startTime,
                rollingBufferEnd: endTime,
            }).pipe(map((data) => ({ ...data, isOngoingRecording: true })))
        } else {
            return this._resolveDvrInfo({
                isChromecastConnected,
                playbackId,
                playbackUrl,
                chromecastPlaybackId,
                chromecastPlaybackUrl,
            }).pipe(
                mergeMap(({ dvrPlaybackId, dvrPlaybackUrl } = {}) => {
                    this.logger.info('request recording URL', recordingId)
                    this.reportActivityApi.setContentType = ReportActivityApi.CONTENT_TYPE.DVR_EVENT
                    this.reportActivityApi.setContentId = recordingId

                    return this._getContentToken({
                        contentId: dvrPlaybackId,
                        playbackUrl: dvrPlaybackUrl,
                        assetId: recordingId,
                        isChromecastConnected,
                    }).pipe(
                        catchError((error = {}) => {
                            if (
                                error.code === VoltError.codes.PLAYER_ENTITLEMENT_REVOKED.code &&
                                error.httpErrorStatus === 403
                            ) {
                                // According to nagra we should differentiate when the player fails to start a PVR playback:
                                // Error 403 - Channel has been removed so the user cannot play a content and have to delete it
                                // Error 500 - User has no right to play the content
                                return throwError(
                                    new VoltError(
                                        VoltError.codes.RECORDING_NOT_AVAILABLE_BECAUSE_CHANNEL_CEASED,
                                        {
                                            inheritedError: error,
                                        }
                                    )
                                )
                            }
                            return throwError(error)
                        })
                    )
                })
            )
        }
    }

    /**
     * Get manifest url for a StartOver
     *
     * @param {Object} options
     * @param {Object} options.playbackUrl Start Over Url
     * @param {Object} options.playbackId Start Over Url
     * @param {Object} options.rollingBufferTime Program Start Time in ms
     * @param {Object} options.programId Platform Program identifier
     * @param {String} data.channelTechnicalId
     * @param {Object} [options.channelId] Platform Channel identifier
     * @param {Boolean} [data.isChromecastConnected]
     */
    getStartOverStreamUrl(args = {}) {
        const {
            playbackId,
            playbackUrl,
            programId,
            channelId,
            channelTechnicalId,
            rollingBufferTime,
            channelSourceId,
            isChromecastConnected,
        } = args

        if (!rollingBufferTime) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_ROLLING_BUFFER_INVALID_TIME, {
                    inheritedError: 'Invalid rollingBufferTime',
                })
            )
        }

        return this._resolveChannelInfo({
            isChromecastConnected,
            channelSourceId,
            channelTechnicalId: channelTechnicalId || channelId,
            playbackId: playbackId,
            playbackUrl: playbackUrl,
        }).pipe(
            mergeMap(({ platformId, drmId, manifestUrl } = {}) => {
                this.reportActivityApi.setContentType =
                    ReportActivityApi.CONTENT_TYPE.LIVE_STCU_EVENT
                this.reportActivityApi.setContentId = programId || platformId
                return this._getContentToken({
                    contentId: drmId,
                    playbackUrl: this._buildBufferedUrl(manifestUrl, rollingBufferTime),
                    assetId: programId || platformId,
                    playbackId,
                    isChromecastConnected,
                })
            })
        )
    }

    /**
     * Get manifest url of a RollingBuffer (catchup/replay)
     *
     * @throws {Error} if not mediaFile matches
     *
     * @param {Object} options
     * @param {Object} options.playbackUrl Start Over Url
     * @param {Object} options.playbackId Start Over id
     * @param {Object} options.rollingBufferTime Program Start Time in ms
     * @param {Object} options.rollingBufferEnd Program End Time in ms
     * @param {Object} options.programId Platform Program identifier
     * @param {String} data.channelTechnicalId
     * @param {Object} [options.channelId] Platform Channel identifier
     * @param {String} [data.isChromecastConnected]
     * @param {String} [data.channelNumber]
     */
    getRollingBufferStreamUrl(args = {}) {
        const {
            playbackId,
            playbackUrl,
            programId,
            channelId,
            rollingBufferTime,
            rollingBufferEnd,
            channelTechnicalId,
            channelSourceId,
            isChromecastConnected,
        } = args

        if (!rollingBufferTime) {
            return throwError(
                new VoltError(VoltError.codes.PLAYER_ROLLING_BUFFER_INVALID_TIME, {
                    inheritedError: 'Invalid rollingBufferTime',
                })
            )
        }

        return this._resolveChannelInfo({
            isChromecastConnected,
            channelSourceId,
            channelTechnicalId: channelTechnicalId || channelId,
            playbackId: playbackId,
            playbackUrl: playbackUrl,
        }).pipe(
            mergeMap(({ platformId, drmId, manifestUrl } = {}) => {
                this.reportActivityApi.setContentType =
                    ReportActivityApi.CONTENT_TYPE.LIVE_STCU_EVENT
                this.reportActivityApi.setContentId = programId || platformId
                return this._getContentToken({
                    contentId: drmId,
                    playbackUrl: this._buildBufferedUrl(
                        manifestUrl,
                        rollingBufferTime,
                        rollingBufferEnd
                    ),
                    assetId: programId || platformId,
                    playbackId: drmId,
                    isChromecastConnected,
                })
            })
        )
    }

    /**
     * Sends PLAYBACK_START request for user activity reporting
     * @returns {Observable<Boolean>} returns true if request was successful
     */
    notifyStartSessionEvent() {
        return this.reportActivityApi.sendUserActivityReportRequest(
            ReportActivityApi.REPORT_EVENT_TYPE.PLAYBACK_START
        )
    }

    /**
     * Sends PLAYBACK_STOP request for user activity reporting
     * @returns {Observable<Boolean>} returns true if request was successful
     */
    notifyEndSessionEvent() {
        return this.reportActivityApi.sendUserActivityReportRequest(
            ReportActivityApi.REPORT_EVENT_TYPE.PLAYBACK_STOP
        )
    }

    /**
     * Sends PLAYBACK_PAUSE request for user activity reporting
     * @returns {Observable<Boolean>} returns true if request was successful
     */
    notifyPauseSessionEvent() {
        return this.reportActivityApi.sendUserActivityReportRequest(
            ReportActivityApi.REPORT_EVENT_TYPE.PLAYBACK_PAUSE
        )
    }

    /**
     * Sends PLAYBACK_SKIP_AHEAD or PLAYBACK_SKIP_BACK request for user activity reporting
     * depends on difference between timeOffset and previousTimeOffset
     * @param {Object} options
     * @param {Number} options.timeOffset
     * @param {Number} options.previousTimeOffset
     * @returns {Observable<Boolean>} returns true if request was successful
     */
    notifySeekSessionEvent({ timeOffset, previousTimeOffset }) {
        const eventType =
            timeOffset > previousTimeOffset
                ? ReportActivityApi.REPORT_EVENT_TYPE.PLAYBACK_SKIP_AHEAD
                : ReportActivityApi.REPORT_EVENT_TYPE.PLAYBACK_SKIP_BACK
        return this.reportActivityApi.sendUserActivityReportRequest(eventType)
    }

    keepAlive({ sessionId, timeOffset }) {
        this.logger.warn('Feature not yet implemented')
        return of(null)
    }

    // -------------------- TOOLBOX --------------------
    /**
     * build URL from LIVE manifest
     * @param {String} playbackUrl URL
     * @param {Number} startTimeMs Start Time
     * @param {Number} endTimeMs End time
     * @returns {String}
     */
    _buildBufferedUrl(playbackUrl = '', startTimeMs, endTimeMs) {
        // CATCH UP: begin=20220224T060000&end=20220224T063000
        // START OVER: begin=20220224T060000
        // TIME SHIFT: begin=20220224T060000
        if (!startTimeMs && !endTimeMs) return playbackUrl
        return `${playbackUrl}${serialize({
            begin: startTimeMs ? this._formatDate(startTimeMs) : undefined,
            end: endTimeMs ? this._formatDate(endTimeMs) : undefined,
        })}`
    }

    /**
     * Format date for Nagra and manifest retrieval
     * @param {Number} timestampMs Timestamp
     * @returns {String} Nagra Date format compatible
     */
    _formatDate(timestampMs) {
        return timestampMs && new Date(timestampMs).toISOString().split('.')[0].replace(/:|-/g, '')
    }

    /**
     * Get the url a associated Live manifest
     *
     * @param {Object} data
     * @param {String} data.playbackId
     * @param {String} data.playbackUrl
     * @param {String} data.channelTechnicalId
     * @param {String} data.channelSourceId
     * @param {String} data.isChromecastConnected
     * @return {Observable<Object>} { platformId, drmId, manifestUrl }
     */
    _resolveChannelInfo({
        playbackId,
        playbackUrl,
        channelTechnicalId,
        channelSourceId,
        isChromecastConnected,
    } = {}) {
        if (
            channelSourceId &&
            isChromecastConnected &&
            this.config.platform === Constants.platform.ios
        ) {
            return this.metaApi
                .getChannels({
                    sourceIds: channelSourceId,
                    device: Constants.emiDevices.androidPhone, // Optimize
                })
                .pipe(
                    mergeMap((channel) => {
                        if (!channel) {
                            return throwError(
                                new VoltError(VoltError.codes.PLAYER_CONTENT_ID_ERROR, {
                                    extraLog: 'Chromecast: Error while querying channel IOS',
                                })
                            )
                        }
                        const { platformId, technicalId, playbackId, playbackUrl } =
                            Object.values(channel)?.[0] || {}
                        return of({
                            platformId: technicalId || platformId,
                            drmId: playbackId,
                            manifestUrl: playbackUrl,
                        })
                    })
                )
        }
        return of({
            platformId: channelTechnicalId,
            drmId: playbackId,
            manifestUrl: playbackUrl,
        })
    }

    /**
     * Get the url a associated Live manifest
     *
     * @param {Object} data
     * @param {String} data.titleId
     * @param {String} data.programSourceId
     * @param {String} data.playbackContentId
     * @param {String} data.isChromecastConnected
     * @return {Observable<Object>} { vodPlatformId, vodSourceId, vodStreams }
     */
    _resolveVodInfo({
        titleId,
        programSourceId,
        playbackContentId,
        streams,
        isChromecastConnected,
    } = {}) {
        if (titleId && isChromecastConnected && this.config.platform === Constants.platform.ios) {
            return this.metaApi
                .getPrograms({
                    ids: [titleId],
                    isPlatform: true,
                    isVod: true,
                    device: Constants.emiDevices.androidPhone, // Optimize
                })
                .pipe(
                    mergeMap((vodContent) => {
                        if (!vodContent) {
                            return throwError(
                                new VoltError(VoltError.codes.PLAYER_CONTENT_ID_ERROR, {
                                    extraLog: 'Chromecast: Error while querying vod IOS',
                                })
                            )
                        }
                        const { id, platformId, sourceId, streams } = vodContent?.[0] || {}
                        return of({
                            vodPlatformId: platformId || id,
                            vodSourceId: sourceId,
                            vodStreams: streams,
                            vodPlaybackContentId: (streams || []).find(
                                (stream) => stream.type === 0
                            )?.uri, // TODO use constant0
                        })
                    })
                )
        }
        return of({
            vodPlatformId: titleId,
            vodSourceId: programSourceId,
            vodPlaybackContentId: playbackContentId,
            vodStreams: streams,
        })
    }

    /**
     * Get the url a associated Live manifest
     *
     * @param {Object} data
     * @param {String} data.playbackId
     * @param {String} data.playbackUrl
     * @param {String} data.chromecastPlaybackId
     * @param {String} data.chromecastPlaybackUrl
     * @param {String} data.isChromecastConnected
     * @return {Observable<Object>} { dvrPlaybackId, dvrPlaybackUrl, }
     */
    _resolveDvrInfo({
        playbackId,
        playbackUrl,
        chromecastPlaybackId,
        chromecastPlaybackUrl,
        isChromecastConnected,
    } = {}) {
        if (
            chromecastPlaybackId &&
            isChromecastConnected &&
            this.config.platform === Constants.platform.ios
        ) {
            return of({
                dvrPlaybackId: chromecastPlaybackId,
                dvrPlaybackUrl: chromecastPlaybackUrl,
            })
        }
        return of({ dvrPlaybackId: playbackId, dvrPlaybackUrl: playbackUrl })
    }

    /**
     * Get the url a associated Live manifest
     *
     * @param {Object} data
     * @param {String} data.playbackId
     * @param {String} data.playbackUrl
     * @param {String} data.chromecastPlaybackId
     * @param {String} data.chromecastPlaybackUrl
     * @param {String} data.isChromecastConnected
     * @return {Observable<Object>} { dvrPlaybackId, dvrPlaybackUrl, }
     */
    _whatIsMyDrm(isChromecastConnected = false) {
        const { platform } = ConfigHelper.getInstance().getConfig()
        if (isChromecastConnected && this.config.platform === Constants.platform.ios) {
            return PlaybackAttributes.DRM_TYPE.WIDEVINE
        }
        const isAppleDevice = [
            Constants.platform.ios,
            Constants.platform.tvos,
            Constants.platform.safari,
        ].includes(platform)

        return isAppleDevice
            ? PlaybackAttributes.DRM_TYPE.FAIRPLAY
            : PlaybackAttributes.DRM_TYPE.WIDEVINE
    }
}
